<template>
  <div class="mc-table mc-editable-table">
    <div class="d-flex justify-content-between align-items-center pb-3">
      <div class="fw-bold text-nowrap card-title mb-0">
        {{ tableTitle }}
      </div>
      <div class="ml-auto d-flex justify-content-end">
        <template v-for="(btn, index) in buttons">
          <McButton
            v-if="!btn.hide"
            :id="btn.id"
            :key="`button-${index}`"
            :variant="btn.variant"
            :class="btn.class"
            :text="btn.text"
            :icon="btn.icon"
            @click="btn.handleClick"
          />
        </template>

        <McButton
          v-if="selectedIdsLength && canDelete"
          id="mc-delete-btn"
          variant="secondary-red"
          class="me-2"
          :text="$t('global.delete')"
          icon="icon-outline_trash"
          @click="$refs['mc-modal-delete'].show()"
        />

        <McButton
          v-if="canCreate"
          id="mc-add-btn"
          variant="primary"
          :disabled="disabledGlobal"
          :text="$t('global.action.create')"
          icon="icon-fill_plus"
          @click="handleAdd"
        />
      </div>
    </div>

    <div :class="{ 'card p-3 mt-2': withCard }">
      <b-row align-h="end">
        <b-col md="4">
          <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
            <span class="badge btn-success">{{ selectedIdsLength }}</span>
            {{ $t('global.object.selectedRow') }}
            <a class="color-primary" @click="handleUnselectAll">{{
              $t('global.action.clearAll')
            }}</a>
          </h6>
        </b-col>
        <b-col v-if="searchable" md="8" class="flex-row-reverse mc-table-view-wrapper">
          <div class="mc-table-view d-flex flex-row-reverse">
            <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
              <template #prepend>
                <i class="ps-2 mc-icon icon-fill_search"></i>
              </template>
              <b-form-input
                ref="searchInput"
                :placeholder="`${$t('global.search')}...`"
                :disabled="disabledGlobal"
                :value="search"
                @input="handleSearchChange"
              />
              <template v-if="search" #append>
                <button
                  class="clear-search-btn"
                  :disabled="disabledGlobal"
                  type="button"
                  @click="handleClearSearch"
                >
                  <i class="pe-2 mc-icon icon-outline_close"></i>
                </button>
              </template>
            </b-input-group>
          </div>
        </b-col>
      </b-row>
      <FormulateForm
        ref="formRef"
        v-model="formValues"
        name="editable-table-form"
        :errors="formErrors"
        @submit="handleSubmit"
        @failed-validation="handleFailedValidation"
      >
        <div class="table-responsive my-3 border border-1 mc-table-overflow">
          <b-table
            :busy="isBusy"
            :items="items"
            :fields="computedFields"
            :per-page="limit"
            fixed
            show-empty
            class="editable-table"
            responsive="md"
          >
            <!-- Loading -->
            <template #table-busy>
              <div class="__busy">
                <b-spinner class="align-middle mb-2" />
                <strong>Loading...</strong>
              </div>
            </template>

            <!-- Empty Result -->
            <template #empty>
              <div class="text-center my-4">
                {{ $t('global.emptyTable') }}
              </div>
            </template>

            <!-- Slot: Header Checkbox -->
            <template #head(checkbox)>
              <div class="form-check" style="width: 10px">
                <b-form-checkbox
                  v-if="canSelect"
                  v-model="selectedAll"
                  :disabled="disabledGlobal || ids.length == 0"
                  @change="handleSelectAll"
                />
              </div>
            </template>

            <!-- Slot: Header Data -->
            <template #head()="data">
              <div @click="handleSortChange(data)">
                {{ data.label }}
                <template v-if="data.field.isSortable">
                  <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                    <i class="mc-icon icon-outline_chevron-up"></i>
                  </div>
                  <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                    <i class="mc-icon icon-outline_chevron-down"></i>
                  </div>
                </template>
              </div>
            </template>

            <!-- Slot: Cell Checkbox -->
            <template #cell(checkbox)="data">
              <div
                v-if="data.item.isShowCheckbox !== undefined ? data.item.isShowCheckbox : true"
                class="form-check"
              >
                <b-form-checkbox
                  :checked="selectedIds[data.item[trackBy]]"
                  :disabled="disabledGlobal || data.item.disableCheckbox"
                  @change="(event) => handleSelect(event, data)"
                />
              </div>
            </template>

            <!-- Slot: All Cells -->
            <template #cell()="{ index, field, value, item }">
              <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                <FormulateInput
                  v-if="field.key === 'companyName'"
                  v-model="companyNameSelect"
                  type="select-resourceful-v2"
                  name="companyNameSelect"
                  :placeholder="$t('setting.list.managementAsset.gps.companyName')"
                  :trackBy="'companyId'"
                  :optionsGetter="resourcefulQuery.companyName"
                  :optionLabelFn="({ item }) => item.companyName"
                  :fullObject="fullObject.companyName"
                  validation="required:trim"
                  error-behavior="submit"
                  validation-behavior="submit"
                  :multiple="false"
                  :taggable="false"
                  :disabled="isSubmitting"
                  @input="handleInputChange(field.key, $event)"
                />
                <FormulateInput
                  v-else-if="field.key === 'licensePlate'"
                  v-model="licensePlateSelect"
                  type="select-resourceful-v2"
                  name="licensePlateSelect"
                  :placeholder="$t('setting.list.managementAsset.gps.licensePlate')"
                  :trackBy="'id'"
                  :optionsGetter="resourcefulQuery.vehicle"
                  :optionLabelFn="({ item }) => item.licensePlate"
                  :fullObject="fullObject.licensePlate"
                  validation="required:trim"
                  error-behavior="submit"
                  validation-behavior="submit"
                  :multiple="false"
                  :taggable="true"
                  :disabled="isSubmitting"
                  @addTag="tagCustom"
                />
                <FormulateInput
                  v-else-if="field.key === 'vehicleType'"
                  v-model="vehicleTypeSelect"
                  type="select-resourceful-v2"
                  name="vehicleTypeSelect"
                  :placeholder="$t('setting.list.managementAsset.gps.vehicleType')"
                  :trackBy="'vehicleTypeId'"
                  :optionsGetter="resourcefulQuery.vehicleType"
                  :optionLabelFn="({ item }) => item.formattedVehicleType"
                  :fullObject="fullObject.vehicleType"
                  validation="required:trim"
                  error-behavior="submit"
                  validation-behavior="submit"
                  :multiple="false"
                  :taggable="false"
                  :disabled="isSubmitting || isDisable"
                  @addTag="tagCustom"
                />
                <FormulateInput
                  v-else-if="field.key == 'odometer'"
                  ref="odometer-ref"
                  v-model="odometer"
                  placeholder="Odometer"
                  name="Odometer"
                  type="text"
                  :disabled="isSubmitting"
                  validation-behavior="submit"
                  error-behavior="submit"
                  validation="bail|required:trim|number|min:0"
                  :validation-messages="{
                    min: $t('setting.list.managementAsset.gps.validation.odometer'),
                  }"
                />
                <b-form-group
                  v-else-if="field.key == 'startSubscription'"
                  id="startSubscription"
                  class="end-date-sub"
                >
                  <date-picker
                    ref="startSubscription-ref"
                    v-model="startSubscription"
                    format="DD MMM YYYY"
                    value-type="YYYY-MM-DD"
                    :placeholder="$t('setting.list.managementAsset.gps.startDateSub')"
                    :class="{
                      'is-invalid': isEmptyStartSubscription,
                    }"
                    :lang="datePickerLang"
                  />
                  <div class="invalid-feedback">
                    <span v-if="isEmptyStartSubscription">
                      <span class="required"></span>
                      {{ $t('global.validation.required') }}
                    </span>
                  </div>
                </b-form-group>
                <b-form-group
                  v-else-if="field.key == 'endSubscription'"
                  id="endSubscription"
                  class="end-date-sub"
                >
                  <date-picker
                    ref="endSubscription-ref"
                    v-model="endSubscription"
                    format="DD MMM YYYY"
                    value-type="YYYY-MM-DD"
                    :placeholder="$t('setting.list.managementAsset.gps.endDateSub')"
                    :class="{
                      'is-invalid': isEmptyEndSubscription || isValidDateSub,
                    }"
                    :lang="datePickerLang"
                  />
                  <div class="invalid-feedback">
                    <span v-if="isEmptyEndSubscription">
                      <span class="required"></span>
                      {{ $t('global.validation.required') }}
                    </span>
                    <span v-else-if="isValidDateSub">
                      {{
                        `${$t('setting.list.managementAsset.gps.endDateSub')} ${$t(
                          'global.validation.min'
                        )} ${$t('setting.list.managementAsset.gps.startDateSub')}`
                      }}
                    </span>
                  </div>
                </b-form-group>
                <FormulateInput
                  v-else
                  v-bind="{ ...field }"
                  :ref="`${field.key}-ref`"
                  :rowItem="item"
                  :label="null"
                  :name="field.inputName || field.key"
                  :type="field.type || 'text'"
                  :value="getInputInitialValue({ field, item })"
                  :initialObject="field.getInitialObject?.(item)"
                  :disabled="isSubmitting"
                  validation-behavior="submit"
                  error-behavior="submit"
                  :taggable="field.taggable || false"
                  min="0"
                  @input="handleInputChange(field.key, $event)"
                  @keypress="field.isNumber ? isNumber($event) : false"
                />
              </template>

              <!-- Bold First Column -->
              <template v-else-if="field.isRedirectable">
                <div class="fw-bold text-primary">
                  {{ value }}
                </div>
              </template>

              <template v-else-if="field.customColumn && isEditingRow !== index">
                <slot :name="field.key" v-bind="{ index, field, value, item }"></slot>
              </template>

              <template v-else-if="isAdding && field.key == 'latitude' && index === 0">
                {{ latitude != null ? latitude : '-' }}
              </template>

              <template v-else-if="isAdding && field.key == 'longitude' && index === 0">
                {{ longitude != null ? longitude : '-' }}
              </template>

              <template v-else>
                {{ transformCellValue(value, field.key) }}
              </template>
            </template>

            <template #cell(activeOn)="data">
              {{ data.item ? momentDateShortMonth(data.item.activeOn) : '-' }}<br />
              <span v-if="data.item.activeOn" class="text-muted">{{
                data.item ? momentTimeSeconds(data.item.activeOn) : '-'
              }}</span>
            </template>

            <template #cell(lastPacket)="data">
              <div v-if="isAdding && data.index === 0">
                {{ lastPacket ? momentDateShortMonth(lastPacket) : '-' }}<br />
                <span v-if="lastPacket" class="text-muted">{{
                  lastPacket ? momentTimeSeconds(lastPacket) : '-'
                }}</span>
              </div>
              <div v-else>
                {{ data.item ? momentDateShortMonth(data.item.lastPacket) : '-' }}<br />
                <span v-if="data.item.lastPacket" class="text-muted">{{
                  data.item ? momentTimeSeconds(data.item.lastPacket) : '-'
                }}</span>
              </div>
            </template>

            <template #cell(motionStatus)="data">
              <div v-if="isAdding && data.index === 0">
                {{ motionStatus ? motionStatusFormat(motionStatus) : '-' }}
              </div>
              <div v-else>
                {{ data.item.motionStatus ? motionStatusFormat(data.item.motionStatus) : '-' }}
              </div>
            </template>

            <!-- Slot: Change History Column -->
            <template #cell(__history)="data">
              <div v-if="data.item.isShowEdit !== undefined ? data.item.isShowEdit : true">
                <div v-if="isEditingRow == data.index">
                  <McButton
                    variant="secondary"
                    :disabled="disabledGlobal"
                    :text="$t('setting.list.managementAsset.gps.changeHistory')"
                    class="mc-btn__sm mc-btn__table"
                  />
                </div>
                <div v-else>
                  <McButton
                    variant="secondary"
                    :disabled="disabledGlobal"
                    :text="$t('setting.list.managementAsset.gps.changeHistory')"
                    class="mc-btn__sm mc-btn__table"
                    @click="showHistory(data)"
                  />
                </div>
              </div>
            </template>

            <!-- Slot: Move GPS Column -->
            <template #cell(__move)="data">
              <div v-if="data.item.isShowEdit !== undefined ? data.item.isShowEdit : true">
                <div v-if="isEditingRow == data.index">
                  <McButton
                    variant="secondary"
                    :disabled="disabledGlobal"
                    :text="$t('setting.list.managementAsset.gps.moveGPS')"
                    class="mc-btn__sm mc-btn__table"
                  />
                </div>
                <div v-else>
                  <McButton
                    variant="secondary"
                    :disabled="disabledGlobal"
                    :text="$t('setting.list.managementAsset.gps.moveGPS')"
                    class="mc-btn__sm mc-btn__table"
                    @click="showModal(data)"
                  />
                </div>
              </div>
            </template>

            <!-- Slot: Action Column (Last Column) -->
            <template #cell(__action)="data">
              <div v-if="data.item.isShowEdit !== undefined ? data.item.isShowEdit : true">
                <div v-if="isEditingRow == data.index">
                  <b-button
                    variant="outline"
                    class="__accept-button btn"
                    type="submit"
                    @click="submitForm"
                  >
                    <i class="mc-icon icon-fill_checkmark"></i>
                  </b-button>

                  <b-button
                    variant="outline"
                    class="__cancel-button btn"
                    @click="handleEditCancel(data)"
                  >
                    <i class="mc-icon icon-outline_close"></i>
                  </b-button>
                </div>

                <b-button
                  v-else
                  variant="outline"
                  :class="{
                    '__edit-button btn': true,
                    __disabled: disabled || isEditingRow !== null || isBusy,
                  }"
                  @click="handleEditStart(data)"
                >
                  <i class="mc-icon icon-outline_edit"></i>
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </FormulateForm>
      <div v-if="showPagination" class="d-flex flex-wrap flex-md-nowrap">
        <div class="mc-limit">
          <div class="__limit">
            <div>{{ $t('global.text.display') }}</div>
            <b-form-select
              class="form-select form-select-sm __limit--dropdown"
              size="sm"
              :disabled="disabledGlobal"
              :options="limitOptions"
              :value="limit"
              @input="handleLimitChange"
            />
            <div>{{ $t('global.text.data') }}</div>
          </div>

          <div v-if="totalCount === 0" class="__info">
            <strong>{{ totalCount }}</strong> {{ $t('global.object.of') }}
            <strong>{{ totalCount }}</strong> {{ $t('global.object.data') }}.
          </div>

          <div v-else class="__info">
            <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
            {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
            {{ $t('global.text.data') }}
          </div>
        </div>
        <div class="mc-pagination-container">
          <b-pagination
            :disabled="disabledGlobal"
            :per-page="limit"
            :total-rows="totalCount"
            :value="page"
            @change="handlePageChange"
          />
        </div>
      </div>
    </div>
    <b-modal
      id="mc-modal-delete"
      ref="mc-modal-delete"
      class="mc-modal"
      title="Hapus"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
          {{ $t('global.confirmDelete2') }}
        </p>
        <b-list-group v-if="listedDeletedItems" class="mc-delete-list">
          <b-list-group-item v-for="(item, key) in selectedListName" :key="key">
            {{ item }}
          </b-list-group-item>
        </b-list-group>
        <p v-if="secondAlertText" class="mt-3">
          {{ secondAlertText }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
        </div>
      </template>
    </b-modal>

    <ModalMoveGPS :item="detailData" @modalClosed="handleModalClosed" />
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import McButton from '@/components/McButton'
import { Helper } from '@/mixin/helper'

import { GET_SUB_DOMAINS } from '@/services/api/setting/personalization/domainManagement'
import { GET_VEHICLE } from '@/services/api/master/fleet'
import { GET_VEHICLE_TYPE } from '@/services/api/setting/fleet/fleetType'

import ModalMoveGPS from './moveGps'
import datePickerLang from '@/helpers/date-picker-lang'

export default {
  components: {
    McButton,
    ModalMoveGPS,
  },
  mixins: [Helper],
  props: {
    fields: { required: true },
    buttons: { required: false },
    apiClient: { required: true },
    trackBy: { default: 'id' },
    limitOptions: {
      default: () => [10, 25, 50, 100],
    },
    searchable: { type: Boolean },
    busy: { type: Boolean },
    tableTitle: { type: String },
    withCard: { type: Boolean },
    disabled: { type: Boolean },
    showNoData: { type: Boolean },
    formErrors: {},
    listedDeletedItems: { type: Boolean },
    deletedItemsKey: { type: String },
    secondAlertText: { type: String },
    showPagination: { type: Boolean, default: true },
    canCreate: { type: Boolean, default: true },
    canDelete: { type: Boolean, default: true },
    canSelect: { type: Boolean, default: true },
    isPredefined: { type: Boolean, default: false },
    fieldException: { type: Array, required: false },
    customToast: { type: Function },
    idIsString: { default: false },
  },
  data() {
    return {
      page: 1,
      currentPage: 1,
      limit: this.limitOptions[0] ?? 10,
      totalCount: 0,
      isBusy: true,
      search: '',
      sorts: [],
      items: [],
      ids: [],
      rowUpdate: {},
      isEditingRow: null,
      isAdding: false,
      isSubmitting: false,
      selectedIds: {},
      selectedIdsLength: 0,
      selectedListName: [],
      selectedAll: false,
      formValues: {},
      countWithoutCheckbox: 0,
      sorting: 'asc',

      initialObject: {
        licensePlate: null,
        companyName: null,
        vehicleType: null,
      },
      licensePlateValue: null,
      licensePlate: [],
      licensePlateSelect: null,
      selectedLicensePlate: null,
      companyName: [],
      companyNameSelect: null,
      selectedCompanyName: null,
      vehicleType: [],
      vehicleTypeSelect: null,
      selectedVehicleType: null,
      fullObject: {
        licensePlate: {},
        companyName: {},
        vehicleType: {},
      },

      latitude: null,
      longitude: null,
      lastPacket: null,
      motionStatus: null,
      odometer: null,

      isDisable: false,

      detailData: null,

      sorted: null,
      endSubscription: null,
      startSubscription: null,
      isEmptyEndSubscription: false,
      isEmptyStartSubscription: false,
      isValidDateSub: false,
    }
  },

  computed: {
    disabledGlobal() {
      return this.disabled || this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    // Fields with added defaults
    computedFields() {
      return [
        ...this.fields,
        {
          key: '__history',
          label: '',
          thClass: 'mc-table--170',
        },
        {
          key: '__move',
          label: '',
          thClass: 'mc-table--130',
        },
        {
          key: '__action',
          label: '',
          thClass: 'mc-action-col-head mc-table--100',
          tdClass: 'mc-action-cell',
        },
      ]
    },

    resourcefulQuery() {
      return {
        companyName: async (opts) => {
          const searchValue = opts && opts.search ? opts.search : ''
          const filter = {
            orderBy: [JSON.stringify({ name: 'asc' })],
            search: searchValue,
            includeParent: true,
          }
          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_SUB_DOMAINS,
            variables: {
              filter: filter,
            },
          })

          const data = res.data.domainManagements.domainManagements.data
          const self = this
          self.companyName = data
          self.initialObject.companyName = self.companyName
          const result = {
            metadata: {
              total_page: 1,
              total_count: 5,
              page: 1,
            },
            data: {
              paginated_result: data,
            },
          }

          return result
        },
        vehicle: async (opts) => {
          const searchValue = opts && opts.search ? opts.search : ''
          const filter = {
            selectedCompanyId: [
              this.companyNameSelect ? this.companyNameSelect : this.getCompanyId(),
            ],
            isHaveDevice: false,
            search: searchValue,
          }
          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_VEHICLE,
            variables: {
              filter: filter,
            },
          })

          const data = res.data.vehicles.vehicles
          const self = this
          self.licensePlate = data
          self.initialObject.licensePlate = self.licensePlate
          const result = {
            metadata: {
              total_page: 1,
              total_count: 5,
              page: 1,
            },
            data: {
              paginated_result: data,
            },
          }

          return result
        },
        vehicleType: async (opts) => {
          const searchValue = opts && opts.search ? opts.search : ''
          const filter = {
            companyId: [this.companyNameSelect ? this.companyNameSelect : this.getCompanyId(), 1],
            search: searchValue,
          }
          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_VEHICLE_TYPE,
            variables: {
              filter: filter,
            },
          })

          const data = res.data.vehicleTypes.vehicleTypes.data
          const self = this
          self.vehicleType = data
          self.initialObject.vehicleType = self.vehicleType
          const result = {
            metadata: {
              total_page: 1,
              total_count: 5,
              page: 1,
            },
            data: {
              paginated_result: data,
            },
          }

          return result
        },
      }
    },

    datePickerLang() {
      return datePickerLang()
    },
  },

  watch: {
    licensePlateSelect: {
      deep: true,
      handler(newValue) {
        if (newValue || newValue === 0) {
          const selected = this.licensePlate.filter((item) => item.id === newValue)
          this.fullObject.licensePlate = selected[0]

          this.selectedLicensePlate = selected[0]

          this.latitude = selected[0].vehicleStatus ? selected[0].vehicleStatus.latitude : 0
          this.longitude = selected[0].vehicleStatus ? selected[0].vehicleStatus.longitude : 0
          this.lastPacket = selected[0].vehicleStatus ? selected[0].vehicleStatus.lastPacket : null
          this.motionStatus = selected[0].vehicleStatus
            ? selected[0].vehicleStatus.motionStatus
            : null
          this.odometer = selected[0] ? selected[0].odometer : null

          this.licensePlateValue = selected[0] ? selected[0].licensePlate : null

          const vehicleTypeId = selected[0] ? selected[0].vehicleTypeId : null

          const vehicleTypeSelected = selected[0] ? selected[0].vehicleType?.type : null
          const opts = {
            search: vehicleTypeSelected,
          }
          if (newValue !== 0) {
            this.isDisable = true
            this.resourcefulQuery.vehicleType(opts).then(() => {
              setTimeout(() => {
                this.vehicleTypeSelect = vehicleTypeId
              }, 500)
            })
          }
        } else {
          this.resetFieldValue()
        }
      },
    },
    companyNameSelect: {
      deep: true,
      handler(newValue) {
        const selected = this.companyName.filter((item) => item.companyId === newValue)
        this.fullObject.companyName = selected[0]

        this.selectedCompanyName = selected[0]

        this.isDisable = false
      },
    },
    vehicleTypeSelect: {
      deep: true,
      handler(newValue) {
        const selected = this.vehicleType.filter((item) => item.vehicleTypeId === newValue)
        this.fullObject.vehicleType = selected[0]

        this.selectedVehicleType = selected[0]
      },
    },
    startSubscription: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.isEmptyStartSubscription = false
        }
      },
    },
    endSubscription: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.isEmptyEndSubscription = false
          this.isValidDateSub = false
        }
      },
    },
  },

  async mounted() {
    if (this.showNoData) {
      this.isBusy = false
      return
    }

    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
      sorts: this.stringSorts(),
    })
  },

  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    stringSorts() {
      const arr = this.sorts.map((sort) => {
        const obj = {
          [sort.key]: sort.order,
        }
        return JSON.stringify(obj)
      })

      return arr
    },
    transformCellValue(value, key) {
      if (!value) return '-'
      if (key === 'odometer' && !value) return 0
      if ((key === 'startSubscription' || key === 'endSubscription') && value) {
        return this.momentDateShortMonth(value)
      }
      if (typeof value === 'number' && key !== 'odometer') return value.toLocaleString('id-ID')
      return value
    },
    getInputInitialValue({ item, field }) {
      // get value from callback in field setting
      // can be used for formatting select input
      if (field.customValueFn) {
        return field.customValueFn(item) || false
      }

      // get value from item
      if (typeof item[field.key] !== 'undefined') {
        return item[field.key]
      }

      return false
    },
    // TECHDEBT: Only supports resourceful format
    // getData methods can be improved to support user-supplied data
    async getData(opts) {
      if (opts.sorts.length === 0) {
        this.sorting = false
      }
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      const filter = {
        pagination: {
          page: opts.page,
          show: opts.limit,
        },
        orderBy: opts.sorts,
        search: opts.search,
      }

      if (filter?.orderBy && !filter.orderBy.length) delete filter.orderBy
      if (filter?.search && !filter.search) delete filter.search

      this.isBusy = true
      const res = await this.apiClient.get(filter)
      if (!res) return
      this.items = res.data
      this.ids = res.data
        .filter(
          (x) =>
            (x.isShowCheckbox === undefined && !x.disableCheckbox) ||
            (x.disableCheckbox === undefined && x.isShowCheckbox)
        )
        .map((x) => x[this.trackBy])
      this.page = res.pagination.page
      this.totalCount = res.pagination.total
      this.countWithoutCheckbox = res.data.filter((x) => x.isShowCheckbox == false).length

      if (res.data.length === 0 && !this.search) {
        this.$emit('available-change', false)
      }
      this.isBusy = false
    },

    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),

    handleClearSearch() {
      this.handleSearchChange(undefined)
    },

    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      })
    },

    async handlePageChange(newPage) {
      this.handleUnselectAll()
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex(
        (sort) => sort.key === data.field.keyOrder
      )
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.keyOrder, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]

      await this.$nextTick()

      return this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.stringSorts(),
      })
    },
    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return
      // refetch single data from backend for data integrity
      const refetchedData = data.item
      if (!refetchedData) return
      if (this.isPredefined && data.item.companyId == 1) {
        this.fields.forEach((item) => {
          if (!this.fieldException.includes(item.key)) {
            item.isEditable = false
          }
        })
      } else if (this.isPredefined) {
        const mapFields = [
          'vendorType',
          'deviceName',
          'phoneNo',
          'odometer',
          'startSubscription',
          'endSubscription',
        ]
        this.fields.forEach((item) => {
          item.isEditable = mapFields.includes(item.key)
        })
      }
      // update the refetched data (row) in the table
      // map is used since 'this.computedItems[index] = {}' will not work
      this.items = this.items.map((item) => {
        if (item[this.trackBy] == data.item[this.trackBy]) return refetchedData
        return item
      })

      this.isEditingRow = data.index
      await this.$nextTick()
      this.$emit('changeData')
      this.$refs[`${this.fields[0].key}-ref`]?.$el.querySelector('input').focus()
      this.odometer = data.item.odometer
      this.startSubscription = data.item.startSubscription
      this.endSubscription = data.item.endSubscription
      this.isEmptyStartSubscription = false
      this.isEmptyEndSubscription = false
      this.isValidDateSub = false
    },
    handleEditCancel() {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      }
      this.isEditingRow = null
      this.isEmptyStartSubscription = false
      this.isEmptyEndSubscription = false
      this.isValidDateSub = false
    },
    // TECHDEBT: transformFields will be on formulate input instead of here
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: typeof value === 'string' ? value.trim() : value }
        })
      )
    },
    async submitForm() {
      if (this.startSubscription && !this.endSubscription) {
        this.isEmptyStartSubscription = false
        this.isEmptyEndSubscription = true
        return
      }

      if (this.endSubscription && !this.startSubscription) {
        this.isEmptyStartSubscription = true
        this.isEmptyEndSubscription = false
        return
      }

      // Add this new check
      if (this.startSubscription && this.endSubscription) {
        if (new Date(this.endSubscription) < new Date(this.startSubscription)) {
          this.isValidDateSub = true
          return
        }
      }

      this.isEmptyStartSubscription = false
      this.isEmptyEndSubscription = false
      this.isValidDateSub = false
    },
    async handleSubmit(data) {
      let dataKey = Object.keys(data)
      if (dataKey.length > 1) {
        dataKey = dataKey[0]
      }
      if (!data[dataKey]) {
        return false
      }

      if (this.isSubmitting) {
        this.$mcToast.warning(this.$t('setting.list.managementAsset.gps.message.pleaseWait'))
        return
      }

      if (this.isEmptyStartSubscription || this.isEmptyEndSubscription || this.isValidDateSub) {
        return
      }

      this.isSubmitting = true

      // Cast fields to correct types
      const transformedData = this.transformFields(data, this.fields)

      let newData = {}

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }

      if (!newData) {
        this.isSubmitting = false
        this.formValues = data

        return
      }

      // Replace data on the table
      if (!this.isAdding) {
        let itemsTemp = [...this.items]
        this.items.splice(0, this.items.length)
        const itemsTempIndex = itemsTemp.findIndex((x) => x[this.trackBy] == newData[this.trackBy])
        itemsTemp[itemsTempIndex] = newData
        await this.$nextTick()
        this.items = itemsTemp
      }

      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
      this.isBusy = false
    },
    async submitStore(data) {
      const storeData = {
        imei: data.imei,
        domainId: data.companyNameSelect,
        vehicleId: data.licensePlateSelect,
        licensePlate: this.licensePlateValue,
        vehicleTypeId: data.vehicleTypeSelect,
        deviceCode: data.deviceName,
        vendorType: data.vendorType,
        phoneNo: data.phoneNo,
        odometer: this.odometer,
        startSubscription: this.startSubscription,
        endSubscription: this.endSubscription,
      }
      const createdData = await this.apiClient.store(storeData)
      if (!createdData) {
        return false
      }
      const refetchedData = createdData
      if (!refetchedData) return false

      this.$mcToast.success(this.$t('setting.list.managementAsset.gps.message.successAdd'))

      this.search = ''
      await this.getData({
        limit: this.limit,
        page: this.page,
        sorts: this.stringSorts(),
      })

      return {
        ...refetchedData,
      }
    },
    async submitUpdate(data, oldData) {
      const storeData = {
        ...data,
        odometer: this.odometer,
        startSubscription: this.startSubscription,
        endSubscription: this.endSubscription,
      }
      const updatedData = await this.apiClient.update(oldData[this.trackBy], oldData, storeData)
      if (!updatedData) return false

      const refetchedData = updatedData
      if (!refetchedData) return false

      this.$mcToast.success(this.$t('setting.list.managementAsset.gps.message.successChange'))

      this.search = ''
      await this.getData({
        limit: this.limit,
        page: this.page,
        sorts: this.stringSorts(),
      })

      return {
        ...refetchedData,
      }
    },
    // eslint-disable-next-line no-unused-vars
    handleFailedValidation(errors) {
      return
      // return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}
      keys.forEach((key) => {
        if (key == this.trackBy) return
        if (oldValue[key] == newValue[key]) return
        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      this.isBusy = true
      let promises = []
      for (let key in this.selectedIds) {
        if (this.selectedIds[key]) {
          promises.push(this.apiClient.delete(key))
        }
      }

      await Promise.all(promises).then((res) => {
        if (this.search) this.handleClearSearch()
        this.handleUnselectAll()
        setTimeout(() => {
          this.getData({
            limit: this.limit,
            page: 1,
            sorts: this.stringSorts(),
          })

          this.$refs['mc-modal-delete'].hide()

          res.forEach((data) => {
            if (!data) {
              if (this.customToast) {
                this.customToast()
              } else {
                this.$mcToast.error('Gagal dihapus')
              }
            } else {
              this.$mcToast.success('Data berhasil dihapus')
              this.selectedListName = []
            }
          })
        }, 300)
      })
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item[this.trackBy]]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.ids.length) this.selectedAll = true

        if (this.listedDeletedItems) {
          this.selectedListName = {
            ...this.selectedListName,
            [data.item[this.trackBy]]: data.item[this.deletedItemsKey],
          }
        }
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1

        if (this.listedDeletedItems) {
          delete this.selectedListName[data.item[this.trackBy]]
        }
      }
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIdsLength = this.ids.length
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true
      this.$emit('available-change', true)
      this.$emit('changeData')
      this.items = [{ [this.trackBy]: this.idIsString ? '' : null }, ...this.items]
      const mapFields = [
        'imei',
        'companyName',
        'licensePlate',
        'vehicleType',
        'vendorType',
        'deviceName',
        'phoneNo',
        'odometer',
        'startSubscription',
        'endSubscription',
      ]
      this.fields.forEach((item) => {
        item.isEditable = mapFields.includes(item.key)
      })

      this.resourcefulQuery.companyName().then(() => {
        // Set companyNameSelect value after the query is executed
        this.companyNameSelect = this.getCompanyId()
      })

      this.resetFieldValue()
      this.isEmptyStartSubscription = false
      this.isEmptyEndSubscription = false
      this.isValidDateSub = false
    },
    handleInputChange(key, newValue) {
      if (key === 'companyName') {
        this.licensePlateSelect = null
        this.vehicleTypeSelect = null
        this.latitude = null
        this.longitude = null
        this.lastPacket = null
        this.motionStatus = null
        this.odometer = null
        this.licensePlateValue = null
      }
      this.$emit('changeData', key, newValue)
    },
    resetFieldValue() {
      this.licensePlateSelect = null
      this.vehicleTypeSelect = null
      this.latitude = null
      this.longitude = null
      this.lastPacket = null
      this.motionStatus = null
      this.odometer = null
      this.licensePlateValue = null
      this.isDisable = false
      this.startSubscription = null
      this.endSubscription = null
    },
    tagCustom(newTag) {
      this.licensePlate = this.licensePlate.filter((item) => item.id !== 0)

      const tag = {
        licensePlate: newTag,
        id: 0,
      }

      this.licensePlate.push(tag)
      this.licensePlateSelected = tag
      this.fullObject.licensePlate = tag

      this.licensePlateSelect = tag.id

      this.latitude = 0
      this.longitude = 0
      this.lastPacket = null
      this.motionStatus = null
      this.odometer = null
      this.vehicleTypeSelect = null
      this.licensePlateValue = tag.licensePlate
      this.isDisable = false
    },
    showModal(data) {
      this.detailData = data.item
      this.$bvModal.show('mc-modal-move_gps')
    },
    async handleModalClosed(data) {
      if (data) {
        this.search = ''
        await this.getData({
          limit: this.limit,
          page: this.page,
          sorts: this.stringSorts(),
        })
      }
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.keyOrder)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.keyOrder && currentColumnSort.order === order
    },
    showHistory(data) {
      this.$router.push({
        name: 'History GPS',
        params: { imei: data.item.imei },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
